import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, inject, OnDestroy, ViewEncapsulation } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { CartItem, GCPImage, ImageCategoryPath } from '../../../constants';
import { FileUpload, FileUploadModule } from 'primeng/fileupload';
import { ImageModule } from 'primeng/image';
import { HttpClient } from '@angular/common/http';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { FirebaseStorageService } from '../../../services/firebase.storage.service';
import { FirebaseFirestoreService } from '../../../services/firebase.firestore.service';
import { onSnapshot } from '@angular/fire/firestore';
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { UtilityService } from '../../../services/utility.service';
import { TextareaModule } from 'primeng/textarea';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { AutoCompleteCompleteEvent, AutoCompleteModule } from 'primeng/autocomplete';
import { takeUntil, tap, map, Subject } from 'rxjs';
@Component({
    selector: 'app-edit-item',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        IftaLabelModule,
        InputTextModule,
        InputNumberModule,
        FileUploadModule,
        ImageModule,
        ButtonModule,
        MessageModule,
        TextareaModule,
        ProgressSpinnerModule,
        AutoCompleteModule
    ],
    providers: [FormBuilder, HttpClient],
    templateUrl: './edit-item.component.html',
    styleUrl: './edit-item.component.scss',
    host: {
        class: 'edit-cart-item'
    },
    encapsulation: ViewEncapsulation.None
})
export class EditItemComponent implements AfterViewInit, OnDestroy {
  formBuilder = inject(FormBuilder);
  dynamicDialogRef = inject(DynamicDialogRef);
  dynamicDialogConfig = inject(DynamicDialogConfig);
  firebaseStorageService = inject(FirebaseStorageService);
  firebaseFirestoreService = inject(FirebaseFirestoreService);
  utilityService = inject(UtilityService);

  itemForm = this.formBuilder.group({
    name: ['', [Validators.required]],
    quantity: [ '', [Validators.required]],
    store: [''],
    location: [''],
    notes: ['']
  })
  item?: CartItem;
  saving = false;
  readOnly = false;
  uploadingImagesInProgress = false;
  storeSuggestions: string[] = [];
  _storeSuggestions: string[] = [];
  destroy$ = new Subject();

  constructor() {
    this.firebaseFirestoreService.getActiveCartItems().pipe(
      takeUntil(this.destroy$.asObservable()),
      tap((items) => console.log(items) ),
      map((items:CartItem[] ) => {
        return items.reduce((grouped, item) => {
          const key = item.store || '';
          if (!grouped[key]) {
            grouped[key] = [];
          }
          grouped[key].push(item);
          return grouped;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        }, {} as any);
      }),
      tap((groupedItems) =>  {
        this.storeSuggestions = Object.keys(groupedItems).filter((store) => !!store);
        this._storeSuggestions = Object.keys(groupedItems).filter((store) => !!store);
      }),
    ).subscribe();
  }

  ngAfterViewInit() {
    this.item = this.dynamicDialogConfig.data;
    if(this.item) {
      this.updateItemForm(this.item);
      onSnapshot(this.firebaseFirestoreService.getCartItemRef(this.item.id as string), {
        next: (snapshot) => {
          if(snapshot.exists()) {
            this.item = {...this.item, ...snapshot.data() as CartItem};
            this.updateItemForm(this.item);
          } else {
            this.readOnly = true;
            this.itemForm.disable();
          }
        }
      })
    }
  }

  ngOnDestroy() {
    this.destroy$.next(null);
  }

  updateItemForm(data: CartItem) {
    if(!data) return;
    this.itemForm.patchValue({
      name: data.name,
      quantity: `${data.quantity} ${data.unit}`,
      store: data.store || '',
      location: data.location || '',
      notes: data.notes || ''
    })
  }

  async deleteImage(image: GCPImage) {
    const data = await this.firebaseStorageService.deleteImage(image);
    if(data) {
      const images = this.item?.images?.filter((item) => item.downloadUrl !== image.downloadUrl );
      await this.firebaseFirestoreService.updateCartItem(this.item?.id as string, {images});
    }
  }

  async uploadImage({files}: {files: Array<File>}, fileupload: FileUpload) {
    console.log(files);
    this.uploadingImagesInProgress = true;
    const images = this.item?.images || [];
    for(let i = 0; i < files.length; i++) {
      const data = await this.firebaseStorageService.uploadImage(files[i], ImageCategoryPath.CartItem);
      if(data) {
        images.push(data);
      }
    }
    fileupload.clear();
    await this.firebaseFirestoreService.updateCartItem(this.item?.id as string, {...this.itemForm.getRawValue() as Partial<CartItem>, unit: '', images});
    this.uploadingImagesInProgress = false;
  }

  async save() {
    this.saving = true;
    const item = this.itemForm.getRawValue() as CartItem;
    await this.firebaseFirestoreService.updateCartItem(this.item?.id as string, {...item, unit: ''});
    this.saving = false;
    this.dynamicDialogRef.close({...this.item, ...item, unit: ''});
  }

  requestCameraPermission() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).ReactNativeWebView?.postMessage(JSON.stringify({action: "request-camera-permissions", data: null}));
  }

  searchStore(event: AutoCompleteCompleteEvent) {
    const storeSuggestions = event.query ? this._storeSuggestions.filter((item) => item.toLowerCase().includes(event.query.toLowerCase())): [...this._storeSuggestions];
    if(storeSuggestions.length === 0) {
      this.storeSuggestions = [event.query];
    } else {
      this.storeSuggestions = storeSuggestions;
    }
  }

}
